import { useMutation, useQueryClient } from "@tanstack/react-query";

import { getAccessTokenCookie } from "../../utils/cookies";
import { getEnv } from "../../utils/env";
// Value import back into the queries layer is safe: the reverse edge is
// `import type`, which is erased at compile time, so there is no runtime cycle.
import { FINALISATION_STATE_KEY } from "../queries/use-finalisation-state";

/**
 * Report finalisation mutations (MDRS-271).
 *
 * The doctor no longer downloads the PDF — they finalise the report, which locks
 * the whole case server-side and flags it for the internal MEDirect team to
 * collect and upload to Vinci. These are hand-rolled `fetch` calls (like the
 * download hooks in `use-templates.ts`) rather than generated-client calls, so a
 * client regen isn't needed for the new endpoints.
 *
 * A 409 from any of these means the case is locked; the caller maps it to a
 * friendly message via `getFriendlyErrorMessage` (which now handles 409/locked).
 * The status code is preserved in the thrown Error message so that mapping works.
 */

const reportGeneratorBase = () => {
  const baseUrl = getEnv("VITE_BACKEND_URL") || "http://localhost:8000";
  return `${baseUrl.replace(/\/$/, "")}/api/v1/report-generator`;
};

const authHeaders = (json = true): Record<string, string> => {
  const token = getAccessTokenCookie();
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const throwFromResponse = async (resp: Response, action: string): Promise<never> => {
  const detail = await resp.text().catch(() => "");
  // Keep the status code in the message so getFriendlyErrorMessage can branch on
  // 409 (locked) vs 403 (permission) vs everything else.
  throw new Error(
    `Failed to ${action} (${resp.status}): ${detail || resp.statusText}`
  );
};

export type ReportFinalisationState = {
  finalised_at: string | null;
  finalised_by_user_id: string | null;
  finalised_pdf_uri: string | null;
  attestation_version: string | null;
  attestation_accepted_at: string | null;
  vinci_upload_status: string | null;
  vinci_uploaded_at: string | null;
  vinci_uploaded_by: string | null;
  vinci_upload_reference: string | null;
};

/**
 * Push a lifecycle mutation's returned state into the `finalisation-state`
 * cache, then invalidate.
 *
 * Writing first matters: invalidation alone leaves a refetch-long window where
 * consumers still read the pre-mutation value, and the editor provider's
 * `handleDownload` — which skips its pre-download save once the case is locked
 * — has no local fallback the way the page's pill does. The invalidate stays as
 * belt-and-braces so anything else on the row re-reads too.
 *
 * All three lifecycle mutations share it so the cache is kept in one place
 * rather than half in the hooks and half at the call sites (MDRS-336).
 */
const syncFinalisationState =
  (queryClient: ReturnType<typeof useQueryClient>) =>
  (data: ReportFinalisationState, { responseId }: { responseId: string }) => {
    queryClient.setQueryData([FINALISATION_STATE_KEY, responseId], data);
    void queryClient.invalidateQueries({
      queryKey: [FINALISATION_STATE_KEY, responseId],
    });
  };

/** Doctor: finalise the report — locks the case, freezes the PDF for Vinci. */
export const useFinaliseReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      responseId,
      attestationVersion,
    }: {
      responseId: string;
      attestationVersion: string;
    }): Promise<ReportFinalisationState> => {
      const url = `${reportGeneratorBase()}/responses/${encodeURIComponent(
        responseId
      )}/finalise`;
      const resp = await fetch(url, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ attestation_version: attestationVersion }),
      });
      if (!resp.ok) return throwFromResponse(resp, "finalise report");
      return resp.json();
    },
    // MDRS-336: several surfaces read the lock state from this query — the
    // "Report Finalised" pill, the read-only editor, and the editor provider's
    // `handleDownload`, which must skip its pre-download save once the case is
    // locked (every write 409s). The query is cached for staleTime (5 min) with
    // no refetch on focus, so without this the app keeps seeing
    // `finalised_at: null` after a successful finalise until a full reload.
    onSuccess: syncFinalisationState(queryClient),
  });
};

/** Admin break-glass: unlock a finalised report (mandatory reason, audit-logged). */
export const useUnlockReport = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      responseId,
      reason,
    }: {
      responseId: string;
      reason: string;
    }): Promise<ReportFinalisationState> => {
      const url = `${reportGeneratorBase()}/responses/${encodeURIComponent(
        responseId
      )}/unlock`;
      const resp = await fetch(url, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ reason }),
      });
      if (!resp.ok) return throwFromResponse(resp, "unlock report");
      return resp.json();
    },
    onSuccess: syncFinalisationState(queryClient),
  });
};

/** Admin: mark the frozen PDF as uploaded to Vinci (interim manual transport). */
export const useMarkVinciUploaded = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      responseId,
      reference,
    }: {
      responseId: string;
      reference?: string;
    }): Promise<ReportFinalisationState> => {
      const url = `${reportGeneratorBase()}/responses/${encodeURIComponent(
        responseId
      )}/mark-vinci-uploaded`;
      const resp = await fetch(url, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(reference ? { reference } : {}),
      });
      if (!resp.ok) return throwFromResponse(resp, "mark uploaded to Vinci");
      return resp.json();
    },
    onSuccess: syncFinalisationState(queryClient),
  });
};
